import React, { useState, useRef, useEffect } from 'react';
import { askRag } from '../api/rag';

const RagChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open && containerRef.current) {
      const textarea = containerRef.current.querySelector('textarea');
      if (textarea) textarea.focus();
    }
  }, [open]);

  const send = async () => {
    const question = input.trim();
    if (!question) return;

    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);

    try {
      const data = await askRag(question, 4); // { answer, sources }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: data.answer || 'No answer returned.',
          sources: data.sources || []
        }
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Error: Could not get response from server.' }
      ]);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send();
  };

  return (
    <>
      {/* Floating Chat Toggle Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setOpen(!open)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition"
        >
          {open ? '✕' : '💬'}
        </button>
      </div>

      {/* Chat Box */}
      {open && (
        <div
          ref={containerRef}
          className="fixed bottom-24 right-6 z-50 w-96 max-h-[70vh] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-xl rounded-lg flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b dark:border-gray-700 flex justify-between items-center">
            <div className="text-sm font-semibold">FreshPress Chat</div>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>

          {/* Messages */}
          <div className="p-3 overflow-y-auto flex-1 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`${m.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  } px-3 py-2 rounded-lg max-w-[80%] whitespace-pre-wrap text-sm`}
                >
                  {m.text}

                  {/* Sources Section */}
                  {m.sources && m.sources.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary>Sources ({m.sources.length})</summary>
                      <ul className="list-disc pl-4 mt-1">
                        {m.sources.map((s, idx) => (
                          <li key={idx}>
                            <a className="underline text-blue-600 dark:text-blue-300" href={s.url} target="_blank" rel="noopener noreferrer">
                              {s.title || s.url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            ))}

            {messages.length === 0 && (
              <div className="text-gray-500 dark:text-gray-400 text-sm">
                Try asking: “What are today’s headlines?” or “Summarize TOI on politics.”
              </div>
            )}
          </div>

          {/* Input Box */}
          <div className="p-3 border-t dark:border-gray-700">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full h-12 p-2 text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded-md resize-none"
              placeholder="Ask FreshPress… (Ctrl/Cmd + Enter to send)"
            />
            <button
              onClick={send}
              disabled={loading}
              className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm disabled:opacity-60"
            >
              {loading ? 'Thinking…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RagChat;
